import { useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Loader2,
  RefreshCw,
  Send,
  Undo2,
  XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { SubmitForReviewModal } from '@/components/PlanDetail/SubmitForReviewModal'
import type { WavePlanDecision, WavePlanDetail } from '@/api/wavePlans'
import { calcPathLabel } from './routes'
import {
  useDecideWavePlan,
  useExportWavePlan,
  useSubmitWavePlan,
  useWithdrawWavePlan,
} from '@/hooks/useWavePlans'
import { WavePlanStatus } from './WavePlanStatus'
import { useReturnFocus } from '@/hooks/useReturnFocus'
import { WavePlanRefreshDialog } from './WavePlanRefreshDialog'
import { WavePlanUndoButton } from './WavePlanUndoButton'
import { staleReasonLabel } from './staleReasons'

const DECISIONS: Record<
  WavePlanDecision,
  { title: string; description: string; confirm: string }
> = {
  approve: {
    title: 'Approve wave plan',
    description:
      'Approving freezes this wave plan. The assortment plan must already be approved.',
    confirm: 'Approve',
  },
  'send-back': {
    title: 'Send back wave plan',
    description: 'The planner can edit and resubmit the wave plan.',
    confirm: 'Send back',
  },
  unapprove: {
    title: 'Unapprove wave plan',
    description:
      'The wave plan becomes editable again. The approved revision is kept for audit.',
    confirm: 'Unapprove',
  },
}

/** Status, staleness and approval actions shared by the wave planning views. */
export function WavePlanWorkflowBar({ detail }: { detail: WavePlanDetail }) {
  const [refreshOpen, setRefreshOpen] = useState(false)
  const [submitOpen, setSubmitOpen] = useState(false)
  const [decision, setDecision] = useState<WavePlanDecision | null>(null)
  const [withdrawOpen, setWithdrawOpen] = useState(false)
  const [notes, setNotes] = useState('')
  const focus = useReturnFocus()
  const open = (show: () => void) => () => {
    focus.remember()
    show()
  }
  const submit = useSubmitWavePlan(detail.id)
  const withdraw = useWithdrawWavePlan(detail.id)
  const decide = useDecideWavePlan(detail.id)
  const exportPlan = useExportWavePlan(detail.id)
  const submission = detail.current_submission
  const pending = submission?.status === 'PENDING'
  const { permissions } = detail

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <WavePlanStatus status={detail.status} />
        <span className="text-xs text-muted-foreground">
          {calcPathLabel(detail.calc_path)} · AP{' '}
          {detail.assortment_plan_status.replace('_', ' ').toLowerCase()}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <WavePlanUndoButton detail={detail} />
          {/* The AP toolbar's Export: every view of the plan has it. */}
          <Button
            size="sm"
            variant="outline"
            title="Download the wave plan as an Excel workbook"
            disabled={exportPlan.isPending}
            onClick={() => exportPlan.mutate(detail.assortment_plan_name)}
          >
            {exportPlan.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            Export
          </Button>
          {permissions.can_refresh && (
            <Button
              size="sm"
              variant="outline"
              onClick={open(() => setRefreshOpen(true))}
            >
              <RefreshCw size={14} /> Refresh from AP
            </Button>
          )}
          {permissions.can_submit && (
            <Button size="sm" onClick={open(() => setSubmitOpen(true))}>
              <Send size={14} /> Submit for approval
            </Button>
          )}
          {permissions.can_withdraw && submission && (
            <Button
              size="sm"
              variant="outline"
              disabled={withdraw.isPending}
              onClick={open(() => setWithdrawOpen(true))}
            >
              <Undo2 size={14} /> Withdraw
            </Button>
          )}
          {permissions.can_review && pending && (
            <>
              <Button size="sm" onClick={open(() => setDecision('approve'))}>
                <CheckCircle2 size={14} /> Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={open(() => setDecision('send-back'))}
              >
                <XCircle size={14} /> Send back
              </Button>
            </>
          )}
          {permissions.can_unapprove && submission && (
            <Button
              size="sm"
              variant="outline"
              onClick={open(() => setDecision('unapprove'))}
            >
              <Undo2 size={14} /> Unapprove
            </Button>
          )}
        </div>
      </div>
      {detail.is_stale && (
        <p
          role="status"
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          The assortment plan changed since the last refresh
          {detail.stale_reasons.length > 0 &&
            ` (${detail.stale_reasons.map(staleReasonLabel).join(', ')})`}
          .{' '}
          {permissions.can_refresh
            ? 'Refresh before submitting.'
            : 'The wave plan must be refreshed and resubmitted.'}
        </p>
      )}
      {detail.blocking_flag_count > 0 && permissions.can_submit && (
        <p className="text-xs text-destructive">
          {detail.blocking_flag_count} blocking{' '}
          {detail.blocking_flag_count === 1 ? 'issue' : 'issues'} must be
          resolved before submitting (see SKU detail).
        </p>
      )}
      {pending && submission && (
        <p className="text-xs text-muted-foreground">
          Submitted by {submission.submitted_by.name ?? 'planner'} · awaiting{' '}
          {submission.reviewers.map(r => r.name ?? r.email).join(', ')}
        </p>
      )}

      <WavePlanRefreshDialog
        planId={detail.id}
        open={refreshOpen}
        onOpenChange={setRefreshOpen}
        onCloseAutoFocus={focus.restore}
      />
      <SubmitForReviewModal
        open={submitOpen}
        onOpenChange={setSubmitOpen}
        planId={detail.assortment_plan_id}
        reviewerRoles={detail.reviewer_roles}
        title="Submit wave plan for approval"
        description="The current calculation is frozen for review. Select the reviewers."
        submitLabel="Submit wave plan"
        onSubmit={payload => submit.mutateAsync(payload)}
        onCloseAutoFocus={focus.restore}
      />
      {withdrawOpen && submission && (
        <ConfirmationDialog
          open
          onOpenChange={setWithdrawOpen}
          title="Withdraw submission"
          description="The wave plan returns to In progress and becomes editable. The reviewers are no longer asked to review it."
          confirmText="Withdraw"
          cancelText="Cancel"
          variant="destructive"
          isLoading={withdraw.isPending}
          onCloseAutoFocus={focus.restore}
          onConfirm={() =>
            withdraw.mutate(submission.id, {
              onSuccess: () => setWithdrawOpen(false),
            })
          }
        />
      )}
      {decision && submission && (
        <ConfirmationDialog
          open
          onOpenChange={open => {
            if (!open) {
              setDecision(null)
              setNotes('')
            }
          }}
          title={DECISIONS[decision].title}
          description={DECISIONS[decision].description}
          confirmText={DECISIONS[decision].confirm}
          cancelText="Cancel"
          isLoading={decide.isPending}
          onCloseAutoFocus={focus.restore}
          variant={decision === 'approve' ? 'default' : 'destructive'}
          onConfirm={() =>
            decide.mutate(
              {
                submissionId: submission.id,
                decision,
                notes: notes.trim() || null,
              },
              {
                onSuccess: () => {
                  setDecision(null)
                  setNotes('')
                },
              }
            )
          }
        >
          <Textarea
            aria-label="Decision notes"
            placeholder="Notes (optional)"
            value={notes}
            maxLength={2000}
            onChange={event => setNotes(event.target.value)}
          />
        </ConfirmationDialog>
      )}
    </div>
  )
}
